import { Link, useLocation } from 'react-router-dom'
import { CalendarClock, FileText, Info, Users, Home, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Settings, Menu, Package } from 'lucide-react'
import { cn } from '@utils/cn'
import { useAuthStore, type UserRole } from '@store/useAuthStore'
import { useLayoutEffect } from 'react'

interface MenuItem {
  icon: React.ReactNode
  label: string
  path?: string
  functionId?: number
  onClick?: () => void
  /** Só estes perfis vêem o item (ex.: só administrador). */
  visibleForRoles?: UserRole[]
  /** Estes perfis não vêem o item (ex.: esconder receção a mecânicos). */
  hiddenForRoles?: UserRole[]
}

interface SidebarProps {
  readonly isOpen: boolean
  readonly onToggle: (isOpen: boolean) => void
  readonly isDesktop?: boolean
}

const ITEM_HEIGHT = 52
const PADDING = 16

export function Sidebar({ isOpen, onToggle, isDesktop = true }: Readonly<SidebarProps>) {
  const location = useLocation()
  const { user, role, getRole } = useAuthStore()
  const effectiveRole: UserRole | null = role ?? (typeof getRole === 'function' ? getRole() : null)

  const handleToggle = () => {
    onToggle(!isOpen)
  }

  const isMechanic = effectiveRole === 'mechanic'
  const workOrdersPath = '/work-orders'
  const clientsPath = isMechanic ? '/mechanic/clients' : '/clients'

  const allMenuItems: Array<MenuItem | null> = [
    {
      icon: <Home className="w-5 h-5" />,
      label: 'Home',
      path: '/homepage',
      hiddenForRoles: ['mechanic'],
    },
    { icon: <FileText className="w-5 h-5" />, label: 'Folhas de Obra', path: workOrdersPath },
    { icon: <Package className="w-5 h-5" />, label: 'Catálogo', path: '/catalog' },
    { icon: <CalendarClock className="w-5 h-5" />, label: 'Revisões', path: '/revisions' },
    { icon: <Info className="w-5 h-5" />, label: 'Dados da Oficina', path: '/workshop', hiddenForRoles: ['mechanic'] },
    {
      icon: <Settings className="w-5 h-5" />,
      label: 'Gestão da Oficina',
      path: '/workshop/management',
      visibleForRoles: ['administrator'],
    },
    { icon: <Users className="w-5 h-5" />, label: 'Clientes', path: clientsPath },
  ]

  const menuItems = allMenuItems
    .filter((item): item is MenuItem => Boolean(item))
    .filter((item) => {
      const currentFunction = user?.functionId
      if (item.visibleForRoles?.length) {
        if (!effectiveRole || !item.visibleForRoles.includes(effectiveRole)) return false
      }
      if (item.hiddenForRoles?.length && effectiveRole && item.hiddenForRoles.includes(effectiveRole)) {
        return false
      }
      if (item.functionId !== undefined) return currentFunction === item.functionId
      return true
    })

  // Altura dinâmica: proporcional ao número de opções
  const mobileDropdownHeight = menuItems.length * ITEM_HEIGHT + PADDING * 2

  // Em mobile: definir CSS var para o Layout ajustar margin-top do content (sync antes do paint)
  useLayoutEffect(() => {
    if (!isDesktop) {
      const value = isOpen ? `${56 + mobileDropdownHeight}px` : '56px'
      document.documentElement.style.setProperty('--sidebar-mobile-height', value)
    }
    return () => {
      document.documentElement.style.removeProperty('--sidebar-mobile-height')
    }
  }, [isDesktop, isOpen, mobileDropdownHeight])

  const renderItemContent = (item: MenuItem) => {
    const isForbiddenPage = document.querySelector('[data-forbidden-page]') !== null
    let isActive = false
    if (!isForbiddenPage && item.path) {
      const hasMoreSpecificMatch = menuItems.some((o) => {
        if (!o.path || !item.path || o.path === item.path) return false
        if (o.path.length <= item.path.length) return false
        return location.pathname === o.path || location.pathname.startsWith(o.path + '/')
      })
      if (!hasMoreSpecificMatch) {
        isActive = location.pathname === item.path || !!(item.path && location.pathname.startsWith(item.path + '/'))
      }
    }

    return (
      <div
        className={cn(
          'flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 cursor-pointer min-h-[44px]',
          { 'bg-white text-[#17253C]': isActive, 'hover:bg-gray-700 text-white': !isActive }
        )}
        onClick={item.onClick}
      >
        <span className={cn('flex-shrink-0', { 'text-[#17253C]': isActive, 'text-gray-400': !isActive })}>
          {item.icon}
        </span>
        <span className="font-medium whitespace-nowrap">{item.label}</span>
      </div>
    )
  }

  if (!isDesktop) {
    return (
      <div className="w-full max-w-[100vw] bg-[#17253C] rounded-b-2xl">
        <div className="bg-[#17253C] text-white overflow-hidden -mt-px rounded-b-2xl">
          {/* Barra com botão de toggle - sempre visível */}
          <button
            onClick={handleToggle}
            className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-[#17253C] hover:bg-[#1f2f4a] transition-colors min-h-[56px]"
            aria-label={isOpen ? 'Fechar menu' : 'Abrir menu'}
          >
            <Menu className="w-4 h-4" />
            <span className="text-sm font-medium">Menu</span>
            {isOpen ? (
              <ChevronUp className="w-4 h-4 ml-1" />
            ) : (
              <ChevronDown className="w-4 h-4 ml-1" />
            )}
          </button>
          {/* Dropdown - altura = número de opções */}
          {isOpen && (
            <nav
              className="px-3 py-2 space-y-1 overflow-hidden bg-[#17253C] rounded-b-2xl"
              style={{ maxHeight: mobileDropdownHeight }}
            >
              {menuItems.map((item) =>
                item.path ? (
                  <Link key={item.path} to={item.path} className="block" onClick={() => onToggle(false)}>
                    {renderItemContent(item)}
                  </Link>
                ) : (
                  <div key={item.label}>{renderItemContent(item)}</div>
                )
              )}
            </nav>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="relative flex items-start h-full">
      <div className="relative h-full">
        <aside
          className={cn(
            'bg-[#17253C] text-white rounded-tr-3xl rounded-br-3xl shadow-lg transition-all duration-300 overflow-hidden h-full',
            isOpen ? 'w-64' : 'w-0'
          )}
        >
          <nav className={cn('px-4 h-full overflow-y-auto py-4 space-y-2')}>
            {menuItems.map((item) =>
              item.path ? (
                <Link key={item.path} to={item.path} className="block">
                  {renderItemContent(item)}
                </Link>
              ) : (
                <div key={item.label}>{renderItemContent(item)}</div>
              )
            )}
          </nav>
        </aside>
        <button
          onClick={handleToggle}
          className="absolute top-1/2 -right-0 transform -translate-y-1/2 translate-x-full bg-[#17253C] text-white rounded-r-lg shadow-lg hover:bg-[#1f2f4a] transition-colors flex items-center justify-center w-3 min-h-12"
          aria-label={isOpen ? 'Fechar menu' : 'Abrir menu'}
        >
          {isOpen ? (
            <ChevronLeft className="w-4 h-4" style={{ marginLeft: '-2px' }} />
          ) : (
            <ChevronRight className="w-4 h-4" style={{ marginLeft: '-2px' }} />
          )}
        </button>
      </div>
    </div>
  )
}
